import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import DOMPurify from 'dompurify';
import { AppLogo } from '@/components/icons/AppLogo';
import { useFooter } from '@/lib/footer/useFooter';
import { useTerritory } from '@/lib/territory/useTerritory';
import { useFooterVisible } from './useFooterVisible';

const sanitize = (html: string) => (html ? DOMPurify.sanitize(html, { ADD_ATTR: ['target', 'rel'] }) : '');

/**
 * Site footer (ARC-147). Renders at the bottom of scrollable page content on
 * standard pages (gated by {@link useFooterVisible}), for signed-in and
 * signed-out players. Content is the Published footer for the resolved country,
 * falling back to the Default — resolution is client-driven: the country comes
 * from `useTerritory()` when known, otherwise the api returns the Default.
 *
 * Content is authored with the same rich-text editor and rendered with the same
 * sanitiser + `.doc-prose` styling as legal documents (links open in a new tab).
 *
 * An optional second section (ARC-171) sits collapsed behind a hardcoded
 * "Read More" link and expands inline; a "Read Less" control collapses it back.
 * The expansion is never persisted: the footer mounts once for the whole app,
 * so a route change won't remount it — an explicit reset keeps every fresh page
 * view collapsed.
 */
export function Footer() {
  const visible = useFooterVisible();
  const country = useTerritory()?.country?.code;
  const { data } = useFooter(country);
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(false);

  const clean = useMemo(() => sanitize(data?.contentHtml ?? ''), [data?.contentHtml]);
  const cleanReadMore = useMemo(() => sanitize(data?.readMoreHtml ?? ''), [data?.readMoreHtml]);

  // Collapse on navigation, adjusted during render (not an effect) so the old
  // page's expanded content never paints for a frame after a route change.
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setExpanded(false);
  }

  if (!visible || !clean) return null;

  return (
    <footer className="bg-surface-soft px-6 pt-8 pb-[calc(96px+env(safe-area-inset-bottom))] md:pb-12">
      <div className="mx-auto flex max-w-2xl flex-col items-start gap-3 text-left">
        <AppLogo width={160} disableAnimation />
        <div className="doc-prose" dangerouslySetInnerHTML={{ __html: clean }} />
        {cleanReadMore && expanded && <div className="doc-prose" dangerouslySetInnerHTML={{ __html: cleanReadMore }} />}
        {cleanReadMore && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            className="text-primary flex items-center gap-2 text-sm underline underline-offset-2"
          >
            {expanded ? 'Read Less' : 'Read More'}
            {expanded ? <ChevronUp className="size-4" aria-hidden /> : <ChevronDown className="size-4" aria-hidden />}
          </button>
        )}
      </div>
    </footer>
  );
}
